/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import EditIcon from '@atlaskit/icon/core/edit';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { EditsList } from './EditsList';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { messages } from './ManageMemoryEditsSubscreen.messages';
import type { MemoryEditItem } from './types';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		minHeight: '0px',
		overflow: 'clip',
		overflowClipMargin: token('space.100'),
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	headline: {
		flexShrink: 0,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderRadius: token('radius.large'),
	},
	iconContainer: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.full'),
		color: token('color.text.inverse'),
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
});

type FlagItem = {
	id: number;
	type: 'error';
	title: string;
};

export interface ManageMemoryEditsSubscreenProps {
	edits?: MemoryEditItem[];
	isLoading?: boolean;
	isError?: boolean;
	onRetry?: () => Promise<void>;
	onDeleteEdit: (editId: string) => Promise<void>;
}

type ListContentProps = {
	isLoading: boolean;
	isError: boolean;
	edits: MemoryEditItem[];
	onRetry: (() => Promise<void>) | undefined;
	editIdConfirming: string | null;
	editIdDeleting: string | null;
	onDeleteClick: (editId: string) => void;
	onCancelConfirm: () => void;
	onConfirmRemove: (editId: string) => void;
};

const ListContent = ({
	isLoading,
	isError,
	edits,
	onRetry,
	editIdConfirming,
	editIdDeleting,
	onDeleteClick,
	onCancelConfirm,
	onConfirmRemove,
}: ListContentProps): JSX.Element => {
	if (isLoading) {
		return <LoadingState />;
	}
	if (isError) {
		return <ErrorState onRetry={onRetry} />;
	}
	if (edits.length === 0) {
		return <EmptyState />;
	}
	return (
		<EditsList
			edits={edits}
			editIdConfirming={editIdConfirming}
			editIdDeleting={editIdDeleting}
			onDeleteClick={onDeleteClick}
			onCancelConfirm={onCancelConfirm}
			onConfirmRemove={onConfirmRemove}
		/>
	);
};

export const ManageMemoryEditsSubscreen = ({
	edits = [],
	isLoading = false,
	isError = false,
	onRetry,
	onDeleteEdit,
}: ManageMemoryEditsSubscreenProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'manageMemoryEditsSubscreen',
		});
	}, [sendAnalyticsEvent]);

	const [flags, setFlags] = useState<FlagItem[]>([]);
	const flagIdCounter = useRef(0);
	const [editIdConfirming, setEditIdConfirming] = useState<string | null>(null);
	const [editIdDeleting, setEditIdDeleting] = useState<string | null>(null);

	const addFlag = useCallback((type: FlagItem['type'], title: string) => {
		const id = flagIdCounter.current++;
		setFlags((prev) => [{ id, type, title }, ...prev].slice(0, 5));
	}, []);

	const handleDismissFlag = useCallback((id: string | number) => {
		setFlags((prev) => prev.filter((f) => f.id !== Number(id)));
	}, []);

	const handleDeleteClick = useCallback(
		(editId: string) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deleteMemoryEdit',
				source: 'manageMemoryEditsSubscreen',
			});
			setEditIdConfirming(editId);
		},
		[sendAnalyticsEvent],
	);

	const handleCancelConfirm = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'cancelDeleteMemoryEdit',
			source: 'manageMemoryEditsSubscreen',
		});
		setEditIdConfirming(null);
	}, [sendAnalyticsEvent]);

	const handleConfirmRemove = useCallback(
		async (editId: string) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'confirmDeleteMemoryEdit',
				source: 'manageMemoryEditsSubscreen',
			});
			setEditIdDeleting(editId);
			try {
				await onDeleteEdit(editId);
				setEditIdConfirming(null);
			} catch {
				addFlag('error', formatMessage(messages.deleteErrorMessage));
			} finally {
				setEditIdDeleting(null);
			}
		},
		[onDeleteEdit, addFlag, formatMessage, sendAnalyticsEvent],
	);

	return (
		<Box xcss={styles.root} testId="manage-memory-edits-subscreen">
			<Inline xcss={styles.headline} space="space.150" alignBlock="center">
				<Box xcss={styles.iconContainer}>
					<EditIcon label="" size="medium" />
				</Box>
				<Stack space="space.025">
					<Heading size="medium">{formatMessage(messages.title)}</Heading>
					<Text as="p" color="color.text.subtle" size="medium">
						{formatMessage(messages.subtitle)}
					</Text>
				</Stack>
			</Inline>

			<ListContent
				isLoading={isLoading}
				isError={isError}
				edits={edits}
				onRetry={onRetry}
				editIdConfirming={editIdConfirming}
				editIdDeleting={editIdDeleting}
				onDeleteClick={handleDeleteClick}
				onCancelConfirm={handleCancelConfirm}
				onConfirmRemove={handleConfirmRemove}
			/>

			<FlagGroup onDismissed={handleDismissFlag}>
				{flags.map((flag) => (
					<AutoDismissFlag
						key={flag.id}
						id={flag.id}
						icon={<StatusErrorIcon label="error" color={token('color.icon.danger')} />}
						title={flag.title}
					/>
				))}
			</FlagGroup>
		</Box>
	);
};

export default ManageMemoryEditsSubscreen;
